import React from 'react';
import {
  Flex,
  Box,
  Heading,
  Text,
  DataTable,
  LoadingOrbit,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  Progress,
  Switch,
  TagBadge,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  useOrgQuotasQuery,
  useUpdateOrgQuotaMutation,
  useUsageQuotasQuery,
  useUpdateUsageQuotaMutation,
  type OrgQuotaListItem,
  type UsageQuotaListItem,
} from './useSuperadminData';

const DEFAULT_MAX_PROPERTIES = 1;

export function SuperadminOrgQuotas() {
  const [selectedOrg, setSelectedOrg] = React.useState<OrgQuotaListItem | null>(null);
  const [maxPropertiesInput, setMaxPropertiesInput] = React.useState('');
  const [formError, setFormError] = React.useState<string | null>(null);

  const [page, setPage] = React.useState(1);
  const quotasQuery = useOrgQuotasQuery({ page, pageSize: 20 });
  const updateQuotaMutation = useUpdateOrgQuotaMutation();

  React.useEffect(() => {
    if (selectedOrg) {
      const currentMax = selectedOrg.quota?.maxProperties ?? DEFAULT_MAX_PROPERTIES;
      setMaxPropertiesInput(String(currentMax));
    } else {
      setMaxPropertiesInput('');
    }
  }, [selectedOrg]);

  const handleSave = () => {
    setFormError(null);
    if (!selectedOrg) {
      setFormError('Please select an organization');
      return;
    }

    const parsed = parseInt(maxPropertiesInput, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      setFormError('Max properties must be a non-negative integer');
      return;
    }

    updateQuotaMutation.mutate(
      { orgId: selectedOrg.org.id, maxProperties: parsed },
      {
        onSuccess: () => {
          setFormError(null);
          // Update selected org with new values after success
          setSelectedOrg((prev) =>
            prev
              ? {
                  ...prev,
                  quota: {
                    id: prev.quota?.id ?? '',
                    maxProperties: parsed,
                    createdAt: prev.quota?.createdAt ?? new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  },
                }
              : null
          );
        },
        onError: (error) => {
          setFormError(error instanceof Error ? error.message : 'Failed to update quota');
        },
      }
    );
  };

  const columns: ColumnDef<StockFeatures, OrgQuotaListItem>[] = React.useMemo(
    () => [
      {
        accessorKey: 'org.name',
        header: 'Organization',
        cell: ({ row }) => <Text weight="medium">{row.original.org.name}</Text>,
      },
      {
        accessorKey: 'propertyCount',
        header: 'Properties',
        cell: ({ row }) => {
          const current = row.original.propertyCount;
          const max = row.original.quota?.maxProperties ?? DEFAULT_MAX_PROPERTIES;
          const isAtLimit = current >= max;
          return (
            <Flex align="center" gap="sm">
              <Text size="sm">
                {current} / {max}
              </Text>
              {isAtLimit && <TagBadge name="At limit" color="yellow" hideDot />}
            </Flex>
          );
        },
      },
      {
        accessorKey: 'quota.maxProperties',
        header: 'Max Properties',
        cell: ({ row }) => (
          <Text size="sm">{row.original.quota?.maxProperties ?? DEFAULT_MAX_PROPERTIES}</Text>
        ),
      },
      {
        accessorKey: 'quota.updatedAt',
        header: 'Last Updated',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.quota?.updatedAt
              ? new Date(row.original.quota.updatedAt).toLocaleString()
              : '(default)'}
          </Text>
        ),
      },
    ],
    []
  );

  return (
    <Box className="space-y-6">
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Organization Quotas</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Manage property limits per organization. Default is {DEFAULT_MAX_PROPERTIES}{' '}
                property.
              </Text>
            </div>
            {quotasQuery.isLoading && <LoadingOrbit size="sm" />}
          </Flex>
        </CardHeader>
        <CardContent>
          {quotasQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-10">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : quotasQuery.isError ? (
            <Text className="text-destructive">
              {quotasQuery.error instanceof Error
                ? quotasQuery.error.message
                : 'Failed to load quotas'}
            </Text>
          ) : quotasQuery.data?.data?.length ? (
            <DataTable
              columns={columns}
              data={quotasQuery.data.data}
              onRowClick={(item) => setSelectedOrg(item)}
              serverPagination={{
                page: quotasQuery.data.pagination.page,
                pageSize: quotasQuery.data.pagination.pageSize,
                pageCount: quotasQuery.data.pagination.totalPages,
                totalItems: quotasQuery.data.pagination.totalItems,
                onPageChange: setPage,
              }}
            />
          ) : (
            <Text className="text-muted-foreground">No organizations found.</Text>
          )}
        </CardContent>
      </Card>

      <RunQuotaSection />

      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Edit Quota</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                {selectedOrg
                  ? `Editing quota for ${selectedOrg.org.name}`
                  : 'Select an organization from the table above'}
              </Text>
            </div>
            {selectedOrg && (
              <Button
                variant="outline"
                onPress={() => {
                  setSelectedOrg(null);
                  setFormError(null);
                }}
              >
                Clear Selection
              </Button>
            )}
          </Flex>
        </CardHeader>
        <CardContent>
          {selectedOrg ? (
            <Flex direction="column" gap="md">
              <Box className="max-w-xs">
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Max Properties
                </Text>
                <TextField
                  value={maxPropertiesInput}
                  onChange={(value) => setMaxPropertiesInput(value)}
                  inputMode="numeric"
                  placeholder="1"
                />
              </Box>

              <Flex gap="sm" align="center">
                <Text size="sm" className="text-muted-foreground">
                  Current usage: {selectedOrg.propertyCount} properties
                </Text>
              </Flex>

              {formError && <Text className="text-destructive text-sm">{formError}</Text>}

              <Flex gap="sm">
                <Button onPress={handleSave} isDisabled={updateQuotaMutation.isPending}>
                  {updateQuotaMutation.isPending ? 'Saving...' : 'Save Quota'}
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Text className="text-muted-foreground">
              Click on a row in the table above to edit its quota.
            </Text>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

// ============================================================================
// Run Quota (monthly agentic runs) — informational, no enforcement
// ============================================================================

/**
 * Period boundaries are UTC instants (start inclusive, end exclusive).
 * Format in UTC — local-TZ rendering shifts "Jun 1" to "May 31" for
 * western-hemisphere viewers. The end is displayed as the last day
 * inside the period.
 */
function formatPeriod(iso: string, opts?: { exclusiveEnd?: boolean }): string {
  const date = new Date(iso);
  if (opts?.exclusiveEnd) date.setUTCDate(date.getUTCDate() - 1);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function RunQuotaSection() {
  const [page, setPage] = React.useState(1);
  const query = useUsageQuotasQuery({ page, pageSize: 20 });
  const updateMutation = useUpdateUsageQuotaMutation();

  const [selected, setSelected] = React.useState<UsageQuotaListItem | null>(null);
  const [monthStartDayInput, setMonthStartDayInput] = React.useState('');
  const [runQuotaInput, setRunQuotaInput] = React.useState('');
  const [isUnlimited, setIsUnlimited] = React.useState(true);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (selected) {
      setMonthStartDayInput(String(selected.quota.monthStartDay));
      const q = selected.quota.monthlyRunQuota;
      setIsUnlimited(q === null);
      setRunQuotaInput(q === null ? '' : String(q));
      setFormError(null);
    } else {
      setMonthStartDayInput('');
      setRunQuotaInput('');
      setIsUnlimited(true);
      setFormError(null);
    }
  }, [selected]);

  const handleSave = () => {
    if (!selected) return;
    setFormError(null);

    const monthStartDay = parseInt(monthStartDayInput, 10);
    if (Number.isNaN(monthStartDay) || monthStartDay < 1 || monthStartDay > 28) {
      setFormError('Start day must be between 1 and 28');
      return;
    }

    let monthlyRunQuota: number | null = null;
    if (!isUnlimited) {
      const parsed = parseInt(runQuotaInput, 10);
      if (Number.isNaN(parsed) || parsed < 0) {
        setFormError('Quota must be a non-negative integer');
        return;
      }
      monthlyRunQuota = parsed;
    }

    updateMutation.mutate(
      { orgId: selected.org.id, monthStartDay, monthlyRunQuota },
      {
        onError: (err) => {
          setFormError(err instanceof Error ? err.message : 'Failed to save');
        },
        onSuccess: () => {
          setSelected(null);
        },
      }
    );
  };

  const columns: ColumnDef<StockFeatures, UsageQuotaListItem>[] = React.useMemo(
    () => [
      {
        accessorKey: 'org.name',
        header: 'Organization',
        cell: ({ row }) => <Text weight="medium">{row.original.org.name}</Text>,
      },
      {
        id: 'period',
        header: 'Period',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatPeriod(row.original.period.start)} →{' '}
            {formatPeriod(row.original.period.end, { exclusiveEnd: true })}
          </Text>
        ),
      },
      {
        id: 'usage',
        header: 'Agentic runs (period)',
        cell: ({ row }) => {
          const { agenticRuns } = row.original.usage;
          const quota = row.original.quota.monthlyRunQuota;
          if (quota === null) {
            return (
              <Flex align="center" gap="sm">
                <Text size="sm">{agenticRuns}</Text>
                <TagBadge name="Unlimited" color="gray" hideDot />
              </Flex>
            );
          }
          const ratio = quota === 0 ? 1 : agenticRuns / quota;
          const variant: 'default' | 'warning' | 'destructive' =
            ratio >= 1 ? 'destructive' : ratio >= 0.8 ? 'warning' : 'default';
          return (
            <Box className="min-w-[180px]">
              <Flex align="center" justify="between" gap="sm">
                <Text size="sm">
                  {agenticRuns} / {quota}
                </Text>
                {ratio >= 1 && <TagBadge name="Over" color="red" hideDot />}
                {ratio >= 0.8 && ratio < 1 && <TagBadge name="Near" color="yellow" hideDot />}
              </Flex>
              <Progress value={Math.min(1, ratio)} variant={variant} size="sm" />
            </Box>
          );
        },
      },
      {
        accessorKey: 'quota.monthStartDay',
        header: 'Start day',
        cell: ({ row }) => <Text size="sm">{row.original.quota.monthStartDay}</Text>,
      },
    ],
    []
  );

  return (
    <>
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Run Quotas</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Monthly agentic-run count per org (runs with ≥1 L3 step). Informational only — not
                enforced at runtime.
              </Text>
            </div>
            {query.isLoading && <LoadingOrbit size="sm" />}
          </Flex>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <Flex align="center" justify="center" className="py-10">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : query.isError ? (
            <Text className="text-destructive">
              {query.error instanceof Error ? query.error.message : 'Failed to load run quotas'}
            </Text>
          ) : query.data?.data?.length ? (
            <DataTable
              columns={columns}
              data={query.data.data}
              onRowClick={(item) => setSelected(item)}
              serverPagination={{
                page: query.data.pagination.page,
                pageSize: query.data.pagination.pageSize,
                pageCount: query.data.pagination.totalPages,
                totalItems: query.data.pagination.totalItems,
                onPageChange: setPage,
              }}
            />
          ) : (
            <Text className="text-muted-foreground">No organizations found.</Text>
          )}
        </CardContent>
      </Card>

      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Edit Run Quota</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                {selected
                  ? `Editing run quota for ${selected.org.name}`
                  : 'Select an organization from the table above'}
              </Text>
            </div>
            {selected && (
              <Button variant="outline" onPress={() => setSelected(null)}>
                Clear Selection
              </Button>
            )}
          </Flex>
        </CardHeader>
        <CardContent>
          {selected ? (
            <Flex direction="column" gap="md">
              <Box className="max-w-xs">
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Period start day (1–28)
                </Text>
                <TextField
                  value={monthStartDayInput}
                  onChange={setMonthStartDayInput}
                  inputMode="numeric"
                  placeholder="1"
                />
              </Box>

              <Flex align="center" gap="sm">
                <Switch isSelected={isUnlimited} onChange={setIsUnlimited} label="Unlimited" />
              </Flex>

              {!isUnlimited && (
                <Box className="max-w-xs">
                  <Text size="xs" className="text-muted-foreground mb-1 block">
                    Monthly agentic-run quota
                  </Text>
                  <TextField
                    value={runQuotaInput}
                    onChange={setRunQuotaInput}
                    inputMode="numeric"
                    placeholder="1000"
                  />
                </Box>
              )}

              <Text size="sm" className="text-muted-foreground">
                Current period usage: {selected.usage.agenticRuns} agentic runs
              </Text>

              {formError && <Text className="text-destructive text-sm">{formError}</Text>}

              <Flex gap="sm">
                <Button onPress={handleSave} isDisabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Text className="text-muted-foreground">
              Click on a row in the table above to edit its run quota.
            </Text>
          )}
        </CardContent>
      </Card>
    </>
  );
}
